import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { Industry, Role } from "../api/types";
import { homePath } from "../domain/access";
import { useAuth } from "./AuthContext";

/** Guard order (§2.1, §2.2): signed in → password changed → tenant user → role → industry. */

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: `${loc.pathname}${loc.search}` }} />;
  return <>{children}</>;
}

/** mustChangePassword = true → every guarded route goes to /change-password (§2.2). */
export function ForcePasswordChange({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

/** Superadmins have no branch; in pos-web they get the hand-off page to pos-admin (§15). */
export function RequireTenant({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role === "SUPERADMIN") return <Navigate to="/platform" replace />;
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: readonly Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user || !roles.includes(user.role)) return <Navigate to={user ? homePath(user) : "/login"} replace />;
  return <>{children}</>;
}

export function RequireIndustry({ industry, children }: { industry: Industry; children: ReactNode }) {
  const { user } = useAuth();
  if (user?.industry !== industry) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Public auth screens: a signed-in user is sent on (§5.1 "redirect away from this page"). */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (!user) return <>{children}</>;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  const from = (loc.state as { from?: string } | null)?.from;
  return <Navigate to={user.role !== "SUPERADMIN" && from && from.startsWith("/") && !from.startsWith("//") ? from : homePath(user)} replace />;
}
